import React from "react";
import getDisplayName from "react-display-name";
import { DefaultOptions, InMemoryCacheConfig } from "@apollo/client";
import hoistNonReactStatics from "hoist-non-react-statics";
import App, { AppContext, AppProps, AppType } from "next/app";
import Head from "next/head";
import absoluteUrl from "next-absolute-url";

import { AnalyticsProvider, AnalyticsVendors } from "@catchoftheday/analytics";
import { Font, getFontTags } from "@catchoftheday/cg-components";
import {
  ApolloClientRetryOptions,
  withApollo,
} from "@catchoftheday/graphql-utils";

import { Assets } from "@common-vendors/components/Assets";
import { DataDogRUM } from "@common-vendors/components/DataDog";
import { Forter } from "@common-vendors/components/Forter";
import { Kasada } from "@common-vendors/components/Kasada";
import { Normalizer } from "@common-vendors/components/Normalizer";
import { Polyfills } from "@common-vendors/components/Polyfills";
import { SEO, SEOProps } from "@common-vendors/components/SEO";

type CommonVendorsProps = {
  kasada?: boolean;
  host: string;
  seo: SEOProps;
  fonts?: Font | Font[];
};

const CommonVendors = ({
  kasada = true,
  host,
  seo,
  fonts,
}: CommonVendorsProps) => {
  const isLocalDevelopmentEnv =
    process.env.NEXT_PUBLIC_ENV_NAME === "development";
  const isProdEnv =
    process.env.NEXT_PUBLIC_ENV_NAME === "absinthe" ||
    process.env.NEXT_PUBLIC_ENV_NAME === "website-prod";
  const disableLocalKasada =
    isLocalDevelopmentEnv &&
    process.env.NEXT_PUBLIC_DISABLE_LOCAL_KASADA === "true";
  const disableLocalAnalytics =
    isLocalDevelopmentEnv &&
    process.env.NEXT_PUBLIC_DISABLE_LOCAL_ANALYTICS === "true";

  return (
    <>
      <div style={{ display: "none" }}>
        {`App Version: ${process.env.NEXT_PUBLIC_PACKAGE}@${process.env.NEXT_PUBLIC_PACKAGE_VERSION}`}
      </div>
      <Normalizer />
      <Polyfills />
      {kasada && !disableLocalKasada ? <Kasada host={host} /> : null}
      <Assets />
      <Head>
        {getFontTags({ fonts })}
        {isProdEnv ? (
          <script
            type="application/javascript"
            src="https://97d067090164.edge.sdk.awswaf.com/97d067090164/bb2ff3da952f/challenge.js"
            defer
          />
        ) : null}
      </Head>
      <SEO {...seo} />
      {!disableLocalAnalytics && <AnalyticsVendors host={host} />}
      <DataDogRUM />
      <Forter />
    </>
  );
};

type WrappedComponentProps = AppProps & {
  host: string;
  urlString: string;
  origin: string;
};

export type NextAppHOCProps = ApolloClientRetryOptions & {
  graphqlEndpoint?: string | (() => string);
  vendors?: CommonVendorsProps;
  apolloCacheConfig?: InMemoryCacheConfig;
  defaultOptions?: DefaultOptions;
  connectToDevTools?: boolean;
  seo?: Omit<SEOProps, "urlString" | "origin">;
  fonts?: Font | Font[];
};

/**
 * This is a HOC that should wrap all the microfrontend Next __app.tsx components and include common behaviour such as analytics
 */
export const nextAppHOC =
  ({
    graphqlEndpoint = process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT as string,
    vendors,
    apolloCacheConfig,
    defaultOptions,
    connectToDevTools,
    seo,
    fonts,
    retryOptions,
  }: NextAppHOCProps = {}) =>
  <T extends AppType>(Comp: T) => {
    if (!graphqlEndpoint) {
      throw new Error("GRAPHQL_ENDPOINT must be defined");
    }

    function WrappedComponent({
      host,
      origin,
      ...rest
    }: WrappedComponentProps) {
      return (
        <>
          <CommonVendors
            seo={{ ...seo, origin }}
            host={host}
            fonts={fonts}
            {...vendors}
          />
          <AnalyticsProvider>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <Comp {...(rest as any)} host={host} />
          </AnalyticsProvider>
        </>
      );
    }

    async function getInitialProps(appContext: AppContext) {
      const host =
        (appContext.ctx.req?.headers["x-forwarded-host"] as string) ||
        appContext.ctx.req?.headers.host;
      const { ctx } = appContext;
      const { req } = ctx;
      const { origin } = absoluteUrl(req);

      // calls page's `getInitialProps` and fills `appProps.pageProps`
      const [appProps, innerProps] = await Promise.all([
        App.getInitialProps(appContext),
        Comp.getInitialProps?.(appContext),
      ]);

      return { ...appProps, ...innerProps, host, origin };
    }

    hoistNonReactStatics(WrappedComponent, Comp);
    WrappedComponent.displayName = `NextAppHOC(${getDisplayName(Comp)})`;
    WrappedComponent.getInitialProps = getInitialProps;

    return withApollo({
      uri: graphqlEndpoint,
      cacheConfig: apolloCacheConfig,
      defaultOptions,
      connectToDevTools,
      retryOptions,
    })(WrappedComponent as unknown as typeof App) as unknown as AppType; // Force cast, as `nextWithApollo` has excessively strict typing.
  };
